import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { CustomDropdown } from "../../Common/_components/CustomDropdown";
import { ReadOnlyDataRow, FileUploadComponent } from "../../Everou/_components";
import { CapsuleButton, RemoveAction } from "../../Common/_components";
import { CustomModal } from "../../Everou/_components/CustomModal";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { LocationCustomization } from "../../Everou/_models/LocationCustomization";
import { SketchPicker } from "react-color";
import { PMSLocationsActions } from "../_actions/PMSLocationsActions";
// WithOutContext: the app-level DndProvider (WebApp) supplies the backend
import { WithOutContext as ReactTags } from "react-tag-input";
import { VACATION_RENTAL_PLANS } from "../_constants/vacationRentalPlan.constants";
import { LocationCoordinatesInput } from "../../Common/_components/LocationCoordinatesInput";
import { Icons } from "../../Everou/_assets";
import { appPermissions } from "../../Everou/_constants/permissions.constants";
import { TechnicalServiceModule } from "../../Everou/TechnicalServiceModule/TechnicalServiceModule";
import {
    InlineValidatedTextInput,
    SettingsSection,
} from "./_components/PMSLocationSettingsFields";

const TIMEZONE_OPTIONS = moment.tz.names().map(tz => CustomDropdown.buildOption(tz, tz));

export function PMSLocationConfiguration() {

    // LIBRARIES
    const {t} = useTranslation();
    const dispatch = useDispatch();

    //GLOBAL STATE
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    const locationInfo = useSelector(Selectors.getPMSLocationInfo);
    const locations = useSelector(Selectors.getPMSLocations);

    const [tags, setTags] = useState([]);

    let locationCustomization = LocationCustomization();
    locationCustomization = useSelector(Selectors.getPMSLocationCustomization);
    const isUploading = useSelector(Selectors.getPMSCustomizationIsUploading);

    const [accentColor, setAccentColor] = useState((locationCustomization && locationCustomization.accentColor) || "#000000");
    const [headerColor, setHeaderColor] = useState((locationCustomization && locationCustomization.headerColor) ||"#000000");
    const [colorEditorOpen, setColorEditorOpen] = useState(null); // 'header' | 'accent' | null
    const [logoEditorOpen, setLogoEditorOpen] = useState(false);

    const KeyCodes = {
        comma: 188,
        enter: 13,
    };

    // INITIALIZATION
    useEffect(() => {
        var i=0;
        for (i=0; i<locations.items.length; i++) {
            if (locations.items[i].uid === locationUid) {
                setTags(locations.items[i].tags);
                return;
            }
        }
    }, [locations, locationUid])
    // The customization is fetched once per location by PMSLocationDetail,
    // which is the only place this screen renders from, so asking for it
    // again on mount only duplicated the request.

    useEffect(() => {
        setAccentColor(locationCustomization.accentColor || "#000000");
        setHeaderColor(locationCustomization.headerColor || "#000000");
    }, [locationCustomization]);

    //ACTION HANDLERS
    function handleUploadLogo(logo) {
        dispatch(PMSLocationsActions.uploadLogo(logo));
    }

    function handleRemoveLogo() {
        dispatch(PMSLocationsActions.deleteLogo());
    }

    function handleChangeAccentColor(hexColor) {
        dispatch(PMSLocationsActions.updateCustomization(hexColor));
    }
    function handleChangeHeaderColor(hexColor) {
        dispatch(PMSLocationsActions.updateCustomization(undefined, hexColor));
    }

    function handleRemoveAccentColor() {
        dispatch(PMSLocationsActions.updateCustomization(""));
    }
    function handleRemoveHeaderColor() {
        dispatch(PMSLocationsActions.updateCustomization(undefined, ""));
    }

    return (
        <div
            className="
                h-100
                d-flex
                no-gutters
                flex-column
            "
        >
            <SettingsSection>
                {dataRow("Id", locationInfo.locationId)}
                <NameInput
                    {...{
                        key: locationInfo.name,
                        initialValue: locationInfo.name,
                        locationUid,
                    }}
                />
                <TimezoneInput
                    {...{
                        timezone: locationInfo.timezone,
                        locationUid,
                    }}
                />
                {locationInfo.vacationRentalPlan === VACATION_RENTAL_PLANS.PREMIUM_PLAN &&
                    <LocationCoordinatesInput
                        latitude={locationInfo.latitude}
                        longitude={locationInfo.longitude}
                        address={locationInfo.address}
                        onSave={(latitude, longitude, address) => dispatch(
                            PMSLocationsActions.updateCoordinates(latitude, longitude, address, locationUid),
                        )}
                    />
                }
            </SettingsSection>
            {effectiveColorsSection()}
            {tagsSection()}
            {technicalSupportSection()}
            {/* Automatic protection and the door-open alert live in the Alarm
                tab, next to the alarm they configure. */}
        </div>
    )

    function NameInput({ initialValue, locationUid }) {

        //LIBRARIES
        const { t } = useTranslation();
        const dispatch = useDispatch();
    
        //ACTION HADLERS
        function handleSave(text) {
            dispatch(PMSLocationsActions.updateLocationName(text, locationUid));
        }
    
        return (
            <DataWrapper hasSeparator={false}>
                <div
                    className="d-flex align-items-center justify-content-between"
                >
                    <div>
                        {t("global_name")}
                    </div>
                    <InlineValidatedTextInput
                        handleSave={handleSave}
                        initialValue={initialValue}
                        required={true}
                    />
                    
                </div>
            </DataWrapper>
        );
    }

    function TimezoneInput({ timezone, locationUid }) {

        //LIBRARIES
        const { t } = useTranslation();
        const dispatch = useDispatch();

        //ACTION HANDLERS
        function handleChange(value) {
            if (value === timezone)
                return;

            dispatch(PMSLocationsActions.updateTimezone(value, locationUid));
        }

        return (
            <DataWrapper hasSeparator={false}>
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        {t("common.timezone")}
                    </div>
                    <div style={{ width: "15rem", maxWidth: "60%" }}>
                        <CustomDropdown
                            value={timezone}
                            onChange={handleChange}
                            options={TIMEZONE_OPTIONS}
                            mode="to-left"
                        />
                    </div>
                </div>
            </DataWrapper>
        );
    }

    // Read-only view of the colors guests will actually see on this location:
    // the location's own color when set, otherwise the one inherited from the
    // PMS customization template.
    function effectiveColorsSection() {
        return (
            <SettingsSection
                title={t("mod_pms_customization")}
                tooltip={t("mod_pms_location_customization_section_tooltip")}
            >
                {/* Editing here detaches the location from the shared branding
                    one value at a time, which is rarely what someone with
                    several locations wants. Say so before they start, not only
                    in the warning that appears once they open an editor. */}
                <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-2">
                    <img
                        src={Icons.info}
                        alt="info"
                        className="mr-2 flex-shrink-0"
                        style={{ width: "24px", height: "24px" }}
                    />
                    <span>
                        {t("mod_pms_location_customization_scope_note")}
                    </span>
                </div>
                {effectiveColorRow({
                    editorKey: "header",
                    label: t("mod_customization_header_color"),
                    effectiveColor: locationCustomization?.headerColor,
                    ownColor: locationCustomization?.ownHeaderColor,
                    handleRemoveOwnColor: handleRemoveHeaderColor,
                })}
                {effectiveColorRow({
                    editorKey: "accent",
                    label: t("mod_customization_accent_color"),
                    effectiveColor: locationCustomization?.accentColor,
                    ownColor: locationCustomization?.ownAccentColor,
                    handleRemoveOwnColor: handleRemoveAccentColor,
                })}
                {effectiveLogoRow()}
                {colorEditorModal()}
                {logoEditorModal()}
            </SettingsSection>
        );
    }

    // Open the picker popup initialized with the color currently in effect,
    // so editing an inherited color starts from what the guest actually sees.
    function handleOpenColorEditor(editorKey) {
        // headerColor/accentColor already carry the inherited value when the
        // location has none of its own.
        const effectiveColor = editorKey === "header"
            ? locationCustomization?.headerColor
            : locationCustomization?.accentColor;

        const setEditorColor = editorKey === "header" ? setHeaderColor : setAccentColor;
        setEditorColor(effectiveColor || "#000000");
        setColorEditorOpen(editorKey);
    }

    function colorEditorModal() {
        if (!colorEditorOpen)
            return null;

        const isHeader = colorEditorOpen === "header";
        const editorColor = isHeader ? headerColor : accentColor;
        const setEditorColor = isHeader ? setHeaderColor : setAccentColor;
        const ownColor = isHeader
            ? locationCustomization?.ownHeaderColor
            : locationCustomization?.ownAccentColor;
        const effectiveColor = isHeader
            ? locationCustomization?.headerColor
            : locationCustomization?.accentColor;

        function handleCancel() {
            setColorEditorOpen(null);
        }

        function handleConfirm() {
            const handleChangeColor = isHeader
                ? handleChangeHeaderColor
                : handleChangeAccentColor;

            handleChangeColor(editorColor);
            setColorEditorOpen(null);
        }

        return (
            <CustomModal
                show={true}
                centered
                onHide={handleCancel}
                title={isHeader
                    ? t("mod_customization_header_color")
                    : t("mod_customization_accent_color")
                }
                content={
                    <div>
                        {overrideWarning(!ownColor && !!effectiveColor)}
                        <div className="d-flex justify-content-center">
                            <SketchPicker
                                color={editorColor}
                                onChange={color => setEditorColor(color.hex)}
                                presetColors={[]}
                                disableAlpha
                            />
                        </div>
                    </div>
                }
                footer={
                    <>
                        <CapsuleButton
                            text={t("global_cancel")}
                            style="INFO"
                            onClick={handleCancel}
                        />
                        <CapsuleButton
                            text={t("global_save")}
                            style="PRIMARY"
                            onClick={handleConfirm}
                        />
                    </>
                }
            />
        );
    }

    function effectiveColorRow({
        editorKey,
        label,
        effectiveColor,
        ownColor,
        handleRemoveOwnColor,
    }) {
        // The origin comes from the backend (own_* fields): the effective
        // colour arrives already merged with the template, and guessing by
        // comparing both hex values mislabelled a location whose own colour
        // happened to match the inherited one — and, once the template moved
        // to the organization, mislabelled every inherited colour as own,
        // since the personal template is no longer loaded.
        const color = effectiveColor;
        // "Inherited from the PMS" no longer describes anything: the value
        // comes from the location's organization, or from the personal
        // template of whoever administers it.
        const inheritedFrom = locationCustomization?.inheritedFromOrganization;
        const originText = ownColor
            ? t("mod_pms_location_customization_from_location")
            : color
                ? inheritedFrom
                    ? t("mod_pms_location_customization_from_organization", {
                        name: inheritedFrom,
                    })
                    : t("mod_pms_location_customization_from_personal")
                : t("mod_pms_location_customization_default");
        return (
            <div className="mt-2 mb-2 d-flex align-items-center justify-content-between">
                <div>{label}</div>
                <div className="d-flex align-items-center">
                    <span className="text-secondary mr-2">{originText}</span>
                    {color &&
                        <>
                            <div
                                className="border rounded mr-2 cp"
                                style={{
                                    width: "1.5rem",
                                    height: "1.5rem",
                                    backgroundColor: color,
                                }}
                                onClick={() => handleOpenColorEditor(editorKey)}
                            />
                            <span className="text-monospace">
                                {color.toUpperCase()}
                            </span>
                        </>
                    }
                    <span
                        className="cp c-text-primary c-font-weight-medium ml-3"
                        onClick={() => handleOpenColorEditor(editorKey)}
                    >
                        {t("global_edit")}
                    </span>
                    {ownColor &&
                        <div className="ml-3">
                            <RemoveAction
                                label={t("global_remove")}
                                isVisible
                                onClick={handleRemoveOwnColor}
                            />
                        </div>
                    }
                </div>
            </div>
        );
    }

    // Setting a value here silently detaches the location from its
    // organization's (or the admin's) branding, so say it before the change is
    // saved instead of letting the origin label quietly flip afterwards.
    function overrideWarning(isOverriding) {
        if (!isOverriding)
            return null;

        const inheritedFrom = locationCustomization?.inheritedFromOrganization;

        return (
            <div className="alert alert-warning" role="alert">
                {inheritedFrom
                    ? t("mod_pms_location_customization_override_warning_organization", {
                        name: inheritedFrom,
                    })
                    : t("mod_pms_location_customization_override_warning_personal")
                }
            </div>
        );
    }

    function logoEditorModal() {
        if (!logoEditorOpen)
            return null;

        const logo = locationCustomization?.logo;
        const ownLogo = locationCustomization?.ownLogo;

        function handleCancel() {
            setLogoEditorOpen(false);
        }

        // Picking a file uploads it straight away (FileUploadComponent's
        // contract), so the modal has nothing left to confirm afterwards.
        function handleUpload(file) {
            handleUploadLogo(file);
            setLogoEditorOpen(false);
        }

        return (
            <CustomModal
                show={true}
                centered
                onHide={handleCancel}
                title={t("mod_customization_location_logo")}
                content={
                    <div>
                        {overrideWarning(!ownLogo && !!logo)}
                        <FileUploadComponent
                            onUpload={handleUpload}
                            isUploading={isUploading}
                            fileName={logo ? t("global_image_uploaded") : null}
                            onRemove={ownLogo
                                ? () => {
                                    handleRemoveLogo();
                                    setLogoEditorOpen(false);
                                }
                                : null
                            }
                            preview={logo &&
                                <div className="d-flex justify-content-center align-items-center">
                                    <img
                                        className="mw-100 c-mh-15rem rounded border bg-light p-2"
                                        alt={t("mod_customization_location_logo")}
                                        src={logo}
                                    />
                                </div>
                            }
                        />
                    </div>
                }
                footer={
                    <CapsuleButton
                        text={t("global_cancel")}
                        style="INFO"
                        onClick={handleCancel}
                    />
                }
            />
        );
    }

    // The logo works like the colours: the location's own one when it has one,
    // otherwise the template's. Same row shape, with the upload popup behind
    // "Edit" instead of a colour picker — a full upload box per image is what
    // made this screen unreadable in the first place.
    function effectiveLogoRow() {
        const logo = locationCustomization?.logo;
        const ownLogo = locationCustomization?.ownLogo;
        const inheritedFrom = locationCustomization?.inheritedFromOrganization;
        const originText = ownLogo
            ? t("mod_pms_location_customization_from_location")
            : logo
                ? inheritedFrom
                    ? t("mod_pms_location_customization_from_organization", {
                        name: inheritedFrom,
                    })
                    : t("mod_pms_location_customization_from_personal")
                // "Default" reads as if there were a stock logo; there is
                // simply none, here or in the template.
                : t("mod_pms_location_customization_no_logo");

        return (
            <div className="mt-2 mb-2 d-flex align-items-center justify-content-between">
                <div>{t("mod_customization_location_logo")}</div>
                <div className="d-flex align-items-center">
                    <span className="text-secondary mr-2">{originText}</span>
                    {logo &&
                        <img
                            className="border rounded bg-white mr-2 p-1"
                            style={{
                                maxHeight: "2rem",
                                maxWidth: "8rem",
                                objectFit: "contain",
                            }}
                            alt={t("mod_customization_location_logo")}
                            src={logo}
                        />
                    }
                    <span
                        className="cp c-text-primary c-font-weight-medium ml-3"
                        onClick={() => setLogoEditorOpen(true)}
                    >
                        {t("global_edit")}
                    </span>
                    {ownLogo &&
                        <div className="ml-3">
                            <RemoveAction
                                label={t("global_remove")}
                                isVisible
                                onClick={handleRemoveLogo}
                            />
                        </div>
                    }
                </div>
            </div>
        );
    }

    function dataRow(name, data) {
        return <ReadOnlyDataRow
            name={name}
            data={data}
        />;
    }

    function DataWrapper({
        children,
        hasSeparator = true,
        key,
    }) {
        return (
            <div key={key}>
                <div className="p-1">
                    {children}
                </div>
                {hasSeparator && <hr className="m-0"/>}
            </div>
        );
    }

    function tagsSection() {
        return (
            <SettingsSection
                title={t("mod_pms_location_tags_section_title")}
                tooltip={t("mod_pms_location_tags_section_tooltip")}
            >
                {/* The border goes on the input itself (.c-tags-field), not
                    around the whole area: a bordered box inside the card read
                    as a field within a field. `auto`, not `scroll`, so no dead
                    scrollbar shows with two tags. */}
                <div
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                    className="mt-2 c-tags-field"
                >
                    <ReactTags
                        tags={tags?.map((tag) => ({ id: tag, text: tag }))}
                        delimiters={[KeyCodes.comma, KeyCodes.enter]}
                        handleDelete={handleDeleteTag}
                        handleAddition={handleAddTag}
                        handleInputBlur={handleOnBlurTag}
                        placeholder={t("mod_pms_location_tags_add_placeholder")}
                        allowDragDrop={false}
                        inputFieldPosition="top"
                    />
                </div>
            </SettingsSection>
        );
    }

    function handleAddTag({text}) {
        if (text)
            dispatch(PMSLocationsActions.createNewLocationTag(text, locationUid));
    }

    function handleDeleteTag(i) {
        const tag = tags[i];
        dispatch(PMSLocationsActions.deleteLocationTag(tag, locationUid));
    }

    function handleOnBlurTag(text) {
        handleAddTag({text});
    }

    // Same module as the Everou location settings, pointed at the PMS-selected
    // location. The permission comes from the PMS location info (the Everou
    // slice is not hydrated here), and the section is hidden altogether for
    // roles that cannot request support, since the module renders nothing
    // for them and an empty titled card would only raise questions.
    function technicalSupportSection() {
        if (!appPermissions.canRequestSat(locationInfo.permission))
            return null;

        return (
            <SettingsSection title={t("location_info_sat_title")}>
                <TechnicalServiceModule
                    locationUid={locationUid}
                    permission={locationInfo.permission}
                    isEmbedded
                />
            </SettingsSection>
        );
    }

}